// react
import { useCallback, useEffect, useState } from 'react';

// types
import { PlanningActivityPayload, PlanningActivity } from '../@types';

// services
import {
	createActivity,
	deactivateActivity as deactivateActivityService,
	deleteActivity as deleteActivityService,
	fetchPlanningActivities,
	fetchActivity,
	updateActivity,
	ApiResponse,
	QueryParams,
} from '../services/api';

// Types

export interface UsePlanningActivitiesParams {
	workPlanId?: number;
	isEnabled?: boolean;
	projectId?: number;
	query?: QueryParams;
}

export interface UsePlanningActivitiesReturn {
	create: (payload: PlanningActivityPayload) => Promise<ApiResponse<PlanningActivity>>;
	data: PlanningActivity[];
	deactivate: (id: number) => Promise<ApiResponse<PlanningActivity>>;
	delete: (id: number) => Promise<ApiResponse<{ deleted: true }>>;
	error: string | null;
	loading: boolean;
	pagination: ApiResponse<PlanningActivity[]>['pagination'];
	refetch: () => Promise<void>;
	update: (
		id: number,
		payload: Partial<PlanningActivityPayload>
	) => Promise<ApiResponse<PlanningActivity>>;
}

// Hook

export const usePlanningActivities = ({
	workPlanId,
	isEnabled = true,
	projectId,
	query = {},
}: UsePlanningActivitiesParams = {}): UsePlanningActivitiesReturn => {
	const defaultPopulate = 'component,category,expenseType,workPlan,entity';

	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<PlanningActivity[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] =
		useState<ApiResponse<PlanningActivity[]>['pagination']>(undefined);

	const queryKey = JSON.stringify(query);

	// fetch data
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken) {
			return;
		}

		const combinedPopulate = (() => {
			const existing =
				(query.populate as string | undefined)
					?.split(',')
					.map((p) => p.trim())
					.filter(Boolean) || [];
			const defaults = defaultPopulate.split(',');
			const merged = Array.from(new Set([...defaults, ...existing])).join(',');
			return merged;
		})();

		setLoading(true);
		setError(null);

		try {
			const res = await fetchPlanningActivities(
				{ accessToken, idToken },
				{
					...query,
					// Only override workPlan if workPlanId is explicitly provided (not undefined)
					...(workPlanId !== undefined ? { workPlan: workPlanId } : {}),
					project: projectId,
					populate: combinedPopulate,
				}
			);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || []);
				setPagination(res.pagination);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch activities');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, workPlanId, projectId, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && projectId) {
			fetchData();
		}
	}, [isEnabled, fetchData, projectId]);

	// create activity
	const create = useCallback(
		async (payload: PlanningActivityPayload): Promise<ApiResponse<PlanningActivity>> => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await createActivity({ accessToken, idToken }, payload);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to create activity';
				return { error: { id: 'CREATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData]
	);

	// update activity
	const update = useCallback(
		async (
			id: number,
			payload: Partial<PlanningActivityPayload>
		): Promise<ApiResponse<PlanningActivity>> => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await updateActivity({ accessToken, idToken }, id, payload);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to update activity';
				return { error: { id: 'UPDATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData]
	);

	// deactivate activity (soft-delete via status='DEACTIVATED')
	const deactivateActivity = useCallback(
		async (id: number): Promise<ApiResponse<PlanningActivity>> => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await deactivateActivityService({ accessToken, idToken }, id);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to deactivate activity';
				return { error: { id: 'DEACTIVATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData]
	);

	// hard-delete activity (only when status='PLANNED' and unlinked, per backend guard)
	const deleteActivity = useCallback(
		async (id: number): Promise<ApiResponse<{ deleted: true }>> => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await deleteActivityService({ accessToken, idToken }, id);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to delete activity';
				return { error: { id: 'DELETE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData]
	);

	return {
		create,
		data,
		deactivate: deactivateActivity,
		delete: deleteActivity,
		error,
		loading,
		pagination,
		refetch: fetchData,
		update,
	};
};

// Single Activity Hook

export interface UseActivityParams {
	id: number;
	isEnabled?: boolean;
	query?: QueryParams;
}

export interface UseActivityReturn {
	data: PlanningActivity | null;
	error: string | null;
	loading: boolean;
	refetch: () => Promise<void>;
	update: (payload: Partial<PlanningActivityPayload>) => Promise<ApiResponse<PlanningActivity>>;
}

export const useActivity = ({
	id,
	isEnabled = true,
	query = {},
}: UseActivityParams): UseActivityReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<PlanningActivity | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const queryKey = JSON.stringify(query);

	// fetch data
	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken || !id) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchActivity({ accessToken, idToken }, id, query);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || null);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch activity');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, id, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && id) {
			fetchData();
		}
	}, [isEnabled, fetchData, id]);

	// update activity
	const update = useCallback(
		async (payload: Partial<PlanningActivityPayload>) => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			const res = await updateActivity({ accessToken, idToken }, id, payload);

			if (!res.error) {
				await fetchData();
			}

			return res;
		},
		[accessToken, idToken, id, fetchData]
	);

	return {
		data,
		error,
		loading,
		refetch: fetchData,
		update,
	};
};
